/**
 * iter645 — Bulk-edit toolbar that floats into view when one or more
 * menu rows are selected. Foodics-style: Apply % discount,
 * Move-to-category, Activate / Deactivate, Delete.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Percent, FolderInput, Eye, EyeOff, Trash2, X, ChevronDown, Sparkles, Loader2, DollarSign } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

export function BulkEditToolbar({
  selectedCount,
  onClear,
  categories,
  onApplyDiscount,
  onMoveCategory,
  onSetAvailability,
  onDelete,
  onAiFill,
  aiFilling = false,
  onLinkRecipe,
  onSetAutoCost,
  saving = false,
}) {
  const [discountInput, setDiscountInput] = useState('10');
  if (selectedCount === 0) return null;
  return (
    <div
      className="sticky bottom-3 z-20 mx-auto max-w-3xl bg-foreground dark:bg-stone-100 text-background dark:text-stone-900 rounded-2xl shadow-2xl border border-stone-800 dark:border-stone-200 px-4 py-2.5 flex items-center gap-2 flex-wrap"
      data-testid="bulk-edit-toolbar"
    >
      <span className="text-sm font-semibold tabular-nums shrink-0" data-testid="bulk-selected-count">
        {selectedCount} selected
      </span>
      <button
        onClick={onClear}
        className="text-xs text-stone-400 hover:text-white px-1.5 py-0.5 rounded transition shrink-0"
        data-testid="bulk-clear-btn"
        title="Deselect all"
      >
        <X size={12} className="inline mr-0.5" /> Clear
      </button>
      <div className="h-5 w-px bg-stone-600 dark:bg-stone-300 mx-1" />

      {/* Apply % discount */}
      <div className="flex items-center gap-1 shrink-0" data-testid="bulk-discount-group">
        <Percent size={13} className="text-amber-300 dark:text-amber-600" />
        <Input
          type="number"
          step="0.5"
          min="-100"
          max="90"
          value={discountInput}
          onChange={(e) => setDiscountInput(e.target.value)}
          className="h-8 w-16 text-xs bg-stone-800 dark:bg-white text-white dark:text-stone-900 border-stone-700 dark:border-stone-300"
          data-testid="bulk-discount-input"
        />
        <Button
          size="sm"
          variant="secondary"
          className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white border-0"
          onClick={() => onApplyDiscount(parseFloat(discountInput))}
          disabled={saving || !discountInput}
          data-testid="bulk-discount-apply"
        >
          Apply %
        </Button>
      </div>

      <div className="h-5 w-px bg-stone-600 dark:bg-stone-300 mx-1" />

      {/* Move-to-category */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="secondary" className="h-8 text-xs bg-stone-800 dark:bg-white text-white dark:text-stone-900 border-stone-700 dark:border-stone-300" data-testid="bulk-move-trigger">
            <FolderInput size={13} className="mr-1" /> Move <ChevronDown size={11} className="ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
          {categories.map((c) => (
            <DropdownMenuItem
              key={c.id}
              onClick={() => onMoveCategory(c.id)}
              data-testid={`bulk-move-to-${c.id}`}
            >
              {c.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="h-5 w-px bg-stone-600 dark:bg-stone-300 mx-1" />

      {/* Activate / Deactivate */}
      <Button
        size="sm"
        variant="secondary"
        className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white border-0"
        onClick={() => onSetAvailability(true)}
        disabled={saving}
        data-testid="bulk-activate"
      >
        <Eye size={13} className="mr-1" /> Activate
      </Button>
      <Button
        size="sm"
        variant="secondary"
        className="h-8 text-xs bg-stone-700 dark:bg-stone-300 hover:bg-stone-600 dark:hover:bg-stone-400 text-white dark:text-stone-900 border-0"
        onClick={() => onSetAvailability(false)}
        disabled={saving}
        data-testid="bulk-deactivate"
      >
        <EyeOff size={13} className="mr-1" /> Deactivate
      </Button>

      <div className="h-5 w-px bg-stone-600 dark:bg-stone-300 mx-1" />

      {onAiFill && (
        <Button
          size="sm"
          variant="secondary"
          className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white border-0"
          onClick={onAiFill}
          disabled={saving || aiFilling}
          data-testid="bulk-ai-fill"
          title="Auto-fill missing Arabic names, descriptions & allergens"
        >
          {aiFilling ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Sparkles size={13} className="mr-1" />} AI Fill
        </Button>
      )}

      {onLinkRecipe && (
        <Button
          size="sm"
          variant="secondary"
          className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white border-0"
          onClick={onLinkRecipe}
          disabled={saving}
          data-testid="bulk-link-recipe"
          title="Attach a recipe to all selected items and switch them to auto-cost"
        >
          <FolderInput size={13} className="mr-1" /> Link Recipe
        </Button>
      )}

      {onSetAutoCost && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="secondary" className="h-8 text-xs bg-stone-700 hover:bg-stone-800 text-white border-0" disabled={saving} data-testid="bulk-auto-cost">
              <DollarSign size={13} className="mr-1" /> Auto-Cost <ChevronDown size={12} className="ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => onSetAutoCost(true)} data-testid="bulk-auto-cost-on">Turn ON (sync from recipe)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSetAutoCost(false)} data-testid="bulk-auto-cost-off">Turn OFF (manual cost)</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Button
        size="sm"
        variant="secondary"
        className="h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white border-0"
        onClick={onDelete}
        disabled={saving}
        data-testid="bulk-delete"
      >
        <Trash2 size={13} className="mr-1" /> Delete
      </Button>
    </div>
  );
}
